import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Copy, ArrowLeft, Check, X, DollarSign, Users, Percent, Award } from "lucide-react";
import { toast } from "sonner";

const S_STYLES = { pending: "bg-amber-50 text-amber-700 border-amber-200", active: "bg-blue-50 text-blue-700 border-blue-200", paused: "bg-slate-100 text-slate-700 border-slate-200", disabled: "bg-red-50 text-red-700 border-red-200" };
const C_STYLES = { pending: "bg-amber-50 text-amber-700 border-amber-200", payable: "bg-blue-50 text-blue-700 border-blue-200", paid: "bg-emerald-50 text-emerald-700 border-emerald-200", reversed: "bg-red-50 text-red-700 border-red-200", cancelled: "bg-slate-100 text-slate-600 border-slate-200" };
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

// ---------- List ----------
export function AdminAffiliatesList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [summary, setSummary] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (status) p.set("status", status);
    api.get(`/admin/affiliates${p.toString() ? "?" + p : ""}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { api.get("/admin/affiliate-reports/summary").then((r) => setSummary(r.data)); }, []);

  return (
    <div data-testid="admin-affiliates-list">
      <div className="mb-6"><div className="eyebrow mb-2">Affiliates</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Affiliate Program</h1></div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat icon={Users} label="Active affiliates" value={summary.active_affiliates}/>
          <Stat icon={Award} label="Orders (MTD)" value={summary.mtd_orders}/>
          <Stat icon={DollarSign} label="Revenue (MTD)" value={money(summary.mtd_revenue)}/>
          <Stat icon={Percent} label="Commission (MTD)" value={money(summary.mtd_commission)}/>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <Link to="/admin/affiliates/applications"><Button variant="outline" className="border-slate-300" data-testid="applications-btn">Applications</Button></Link>
        <Link to="/admin/affiliates/payouts"><Button variant="outline" className="border-slate-300" data-testid="payouts-btn">Payouts</Button></Link>
        <Link to="/admin/affiliates/settings"><Button variant="outline" className="border-slate-300" data-testid="settings-btn">Settings</Button></Link>
        <div className="flex-1"/>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="h-10 px-3 rounded-md border border-slate-300 text-sm">
          <option value="">All statuses</option><option value="active">Active</option><option value="paused">Paused</option><option value="disabled">Disabled</option>
        </select>
        <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 max-w-xs"/>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-3 py-2">Affiliate</th><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Tier</th>
              <th className="text-right px-3 py-2">MTD orders</th><th className="text-right px-3 py-2">MTD comm.</th>
              <th className="text-right px-3 py-2">Lifetime comm.</th><th className="text-right px-3 py-2">Unpaid</th>
              <th className="text-left px-3 py-2">Status</th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.affiliate_id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer" data-testid={`aff-row-${a.affiliate_id}`} onClick={() => window.location.assign(`/admin/affiliates/${a.affiliate_id}`)}>
                <td className="px-3 py-3"><div className="text-slate-900">{a.first_name} {a.last_name}</div><div className="text-xs text-slate-500">{a.email}</div></td>
                <td className="px-3 py-3 font-mono text-xs">{a.referral_code}</td>
                <td className="px-3 py-3 text-xs">{a.current_tier?.name || "—"} · {a.current_tier?.commission_percent ?? "—"}%</td>
                <td className="px-3 py-3 text-right font-mono text-xs">{a.mtd_orders}</td>
                <td className="px-3 py-3 text-right font-mono text-xs">{money(a.mtd_commission)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs">{money(a.lifetime_commission)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs">{money(a.unpaid)}</td>
                <td className="px-3 py-3"><Badge className={`rounded-md border font-mono text-[10px] uppercase tracking-widest ${S_STYLES[a.status] || ""}`}>{a.status}</Badge></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 text-sm py-10">No affiliates yet. Approve an application to get started.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Stat({ icon: I, label, value }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="w-8 h-8 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center"><I className="w-4 h-4 text-blue-700" strokeWidth={1.75}/></div>
        <div className="text-2xl font-display font-semibold">{value}</div>
      </div>
      <div className="text-xs text-slate-500 mt-2 font-mono uppercase tracking-widest">{label}</div>
    </div>
  );
}

// ---------- Detail ----------
export function AdminAffiliateDetail() {
  const { affiliateId } = useParams();
  const [aff, setAff] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/affiliates/${affiliateId}`).then((r) => setAff(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [affiliateId]);

  if (!aff) return <div className="text-sm text-slate-500 p-6">Loading…</div>;
  const nextTier = aff.next_tier;
  const cur = aff.current_tier;
  const pct = nextTier ? Math.min(100, Math.round((aff.mtd_orders / nextTier.min_orders) * 100)) : 100;

  const setStatus = async (status) => {
    setSaving(true);
    try { await api.patch(`/admin/affiliates/${affiliateId}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-affiliate-detail">
      <Link to="/admin/affiliates" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4"/> Affiliates</Link>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-2">Affiliate</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{aff.first_name} {aff.last_name}</h1>
          <div className="text-sm text-slate-500 mt-1">{aff.email} · Joined {new Date(aff.created_at).toLocaleDateString()}</div>
        </div>
        <div className="flex gap-2 items-center">
          <Badge className={`rounded-md border font-mono text-[10px] uppercase tracking-widest ${S_STYLES[aff.status] || ""}`}>{aff.status}</Badge>
          <select value={aff.status} disabled={saving} onChange={(e) => setStatus(e.target.value)} data-testid="aff-status-select" className="h-9 px-2 rounded-md border border-slate-300 text-sm">
            <option value="active">Active</option><option value="paused">Paused</option><option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="border border-slate-200 rounded-md bg-white p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Referral code</div>
          <div className="flex items-center gap-2">
            <div className="font-mono text-xl font-semibold">{aff.referral_code}</div>
            <button onClick={() => navigator.clipboard.writeText(aff.referral_code).then(() => toast.success("Copied"))} className="p-1 rounded hover:bg-slate-100" data-testid="copy-code"><Copy className="w-3.5 h-3.5 text-slate-500"/></button>
          </div>
          <div className="mt-3">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Referral URL</div>
            <div className="flex items-center gap-2">
              <div className="text-xs text-slate-800 font-mono truncate">{window.location.origin}/api/ref/{aff.referral_code}</div>
              <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/api/ref/${aff.referral_code}`).then(() => toast.success("Copied"))} className="p-1 rounded hover:bg-slate-100"><Copy className="w-3.5 h-3.5 text-slate-500"/></button>
            </div>
          </div>
        </div>
        <div className="border border-slate-200 rounded-md bg-white p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Current tier</div>
          <div className="font-display text-xl font-semibold">{cur?.name || "—"} · {cur?.commission_percent ?? "—"}%</div>
          {nextTier && (
            <div className="mt-3">
              <div className="text-xs text-slate-500 mb-1">{aff.mtd_orders} / {nextTier.min_orders} orders → {nextTier.name} ({nextTier.commission_percent}%)</div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${pct}%` }}/></div>
              <div className="text-xs text-slate-500 mt-1">{aff.mtd_needed_for_next} more to reach {nextTier.name}</div>
            </div>
          )}
        </div>
        <div className="border border-slate-200 rounded-md bg-white p-5 grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-xs font-mono uppercase tracking-widest text-slate-500">Payable</div><div className="font-mono text-lg">{money(aff.payable)}</div></div>
          <div><div className="text-xs font-mono uppercase tracking-widest text-slate-500">Paid</div><div className="font-mono text-lg">{money(aff.paid_out)}</div></div>
          <div><div className="text-xs font-mono uppercase tracking-widest text-slate-500">Lifetime orders</div><div className="font-mono text-lg">{aff.commissions?.filter(c => !["reversed","cancelled"].includes(c.status)).length}</div></div>
          <div><div className="text-xs font-mono uppercase tracking-widest text-slate-500">Lifetime comm.</div><div className="font-mono text-lg">{money(aff.commissions?.reduce((s, c) => !["reversed","cancelled"].includes(c.status) ? s + c.commission_amount : s, 0))}</div></div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md bg-white">
        <div className="px-5 py-3 border-b border-slate-100 font-display text-lg font-semibold">Referred orders & commissions</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Order</th><th className="text-left px-3 py-2">Tier</th><th className="text-right px-3 py-2">Order total</th><th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">Comm.</th><th className="text-left px-3 py-2">Source</th><th className="text-left px-3 py-2">Status</th></tr>
          </thead>
          <tbody>
            {(aff.commissions || []).map((c) => (
              <tr key={c.commission_id} className="border-b border-slate-100 last:border-b-0" data-testid={`comm-${c.commission_id}`}>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 font-mono text-xs"><Link to={`/admin/orders/${c.order_id}`} className="text-blue-700 hover:underline">{c.order_id}</Link></td>
                <td className="px-3 py-2 text-xs">{c.tier_at_time_of_sale || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{money(c.order_total)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{c.commission_rate}%</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{money(c.commission_amount)}</td>
                <td className="px-3 py-2 text-xs">{c.referral_source}</td>
                <td className="px-3 py-2"><Badge className={`rounded-md border font-mono text-[10px] uppercase tracking-widest ${C_STYLES[c.status] || ""}`}>{c.status}</Badge></td>
              </tr>
            ))}
            {(aff.commissions || []).length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 text-sm py-8">No commissions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Applications ----------
export function AdminAffiliateApplications() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState("");

  const load = () => api.get("/admin/affiliate-applications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id, first_name) => {
    const code = window.prompt(`Referral code for ${first_name}? (Uppercase, no spaces)`, first_name.toUpperCase());
    if (!code) return;
    const disc = window.prompt("Customer discount % (e.g. 10)", "10");
    setSaving(id);
    try {
      await api.post(`/admin/affiliate-applications/${id}/approve`, { referral_code: code, customer_coupon_percent: Number(disc || 10), status: "active" });
      toast.success("Approved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    setSaving("");
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this application?")) return;
    await api.post(`/admin/affiliate-applications/${id}/reject`); toast.success("Rejected"); load();
  };

  return (
    <div data-testid="admin-affiliate-applications">
      <Link to="/admin/affiliates" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4"/> Affiliates</Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">Applications</h1>
      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Website</th><th className="text-left px-3 py-2">Audience</th><th className="text-left px-3 py-2">Status</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.application_id} className="border-b border-slate-100 last:border-b-0" data-testid={`app-row-${it.application_id}`}>
                <td className="px-3 py-2">{it.first_name} {it.last_name}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">{it.email}</td>
                <td className="px-3 py-2 text-xs">{it.website || "—"}</td>
                <td className="px-3 py-2 text-xs">{it.audience_size || "—"}</td>
                <td className="px-3 py-2"><Badge className={`rounded-md border font-mono text-[10px] uppercase tracking-widest ${S_STYLES[it.status] || ""}`}>{it.status}</Badge></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {it.status === "pending" && <>
                    <button onClick={() => approve(it.application_id, it.first_name)} disabled={saving === it.application_id} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`approve-${it.application_id}`}><Check className="w-4 h-4"/></button>
                    <button onClick={() => reject(it.application_id)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><X className="w-4 h-4"/></button>
                  </>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 text-sm py-10">No applications yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Payouts ----------
export function AdminAffiliatePayouts() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/affiliate-payouts").then((r) => setItems(r.data)); }, []);
  const markPaid = async (payout_id) => {
    const ref = window.prompt("Reference / transaction ID?", "");
    await api.post(`/admin/affiliate-payouts/${payout_id}/mark-paid${ref ? `?reference_id=${encodeURIComponent(ref)}` : ""}`);
    toast.success("Marked paid"); api.get("/admin/affiliate-payouts").then((r) => setItems(r.data));
  };
  return (
    <div data-testid="admin-affiliate-payouts">
      <Link to="/admin/affiliates" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4"/> Affiliates</Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">Payouts</h1>
      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-3 py-2">Payout ID</th><th className="text-left px-3 py-2">Affiliate</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Method</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Ref</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.payout_id} className="border-b border-slate-100 last:border-b-0">
                <td className="px-3 py-2 font-mono text-xs">{p.payout_id}</td>
                <td className="px-3 py-2 font-mono text-xs"><Link to={`/admin/affiliates/${p.affiliate_id}`} className="text-blue-700 hover:underline">{p.affiliate_id}</Link></td>
                <td className="px-3 py-2 text-right font-mono">{money(p.amount)}</td>
                <td className="px-3 py-2 text-xs">{p.payment_method}</td>
                <td className="px-3 py-2 text-xs">{p.status}</td>
                <td className="px-3 py-2 text-xs">{p.reference_id || "—"}</td>
                <td className="px-3 py-2 text-right">{p.status === "draft" && <Button onClick={() => markPaid(p.payout_id)} data-testid={`mark-paid-${p.payout_id}`} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white">Mark paid</Button>}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 text-sm py-10">No payouts yet. Create one from an affiliate's payable commissions.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Settings ----------
export function AdminAffiliateSettings() {
  const [tiers, setTiers] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadAll = () => Promise.all([
    api.get("/admin/affiliate-tiers").then((r) => setTiers(r.data)),
    api.get("/admin/affiliate-settings").then((r) => setSettings(r.data)),
  ]);
  useEffect(() => { loadAll(); }, []);

  const saveTier = async (t) => {
    try { await api.patch(`/admin/affiliate-tiers/${t.tier_id}`, t); toast.success("Tier saved"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveSettings = async () => {
    try { await api.patch("/admin/affiliate-settings", settings); toast.success("Settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!settings) return null;
  return (
    <div data-testid="admin-affiliate-settings">
      <Link to="/admin/affiliates" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4"/> Affiliates</Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">Program settings</h1>

      <div className="border border-slate-200 rounded-md bg-white p-5 mb-6">
        <div className="font-display text-lg font-semibold mb-3">Tiers</div>
        <table className="w-full text-sm">
          <thead className="text-xs font-mono uppercase tracking-widest text-slate-500"><tr><th className="text-left px-2 py-2">Name</th><th className="text-right px-2 py-2">Min orders</th><th className="text-right px-2 py-2">Max orders</th><th className="text-right px-2 py-2">% Commission</th><th className="text-center px-2 py-2">Active</th><th></th></tr></thead>
          <tbody>
            {tiers.map((t, idx) => (
              <tr key={t.tier_id} className="border-t border-slate-100">
                <td className="px-2 py-2"><Input value={t.name} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} className="h-9 text-sm" data-testid={`tier-name-${idx}`}/></td>
                <td className="px-2 py-2"><Input type="number" value={t.min_orders} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, min_orders: Number(e.target.value) } : x))} className="h-9 text-sm text-right font-mono" data-testid={`tier-min-${idx}`}/></td>
                <td className="px-2 py-2"><Input type="number" value={t.max_orders ?? ""} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, max_orders: e.target.value === "" ? null : Number(e.target.value) } : x))} className="h-9 text-sm text-right font-mono" placeholder="∞"/></td>
                <td className="px-2 py-2"><Input type="number" step="0.1" value={t.commission_percent} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, commission_percent: Number(e.target.value) } : x))} className="h-9 text-sm text-right font-mono" data-testid={`tier-pct-${idx}`}/></td>
                <td className="px-2 py-2 text-center"><input type="checkbox" checked={t.active} onChange={(e) => setTiers(tiers.map((x, i) => i === idx ? { ...x, active: e.target.checked } : x))}/></td>
                <td className="px-2 py-2 text-right"><Button onClick={() => saveTier(t)} data-testid={`save-tier-${idx}`} className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white">Save</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-slate-200 rounded-md bg-white p-5 space-y-3">
        <div className="font-display text-lg font-semibold mb-1">Global settings</div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Cookie duration (days)"><Input type="number" value={settings.cookie_days} onChange={(e) => setSettings({...settings, cookie_days: Number(e.target.value)})} data-testid="set-cookie-days"/></FormField>
          <FormField label="Commission hold (days)"><Input type="number" value={settings.hold_days} onChange={(e) => setSettings({...settings, hold_days: Number(e.target.value)})} data-testid="set-hold-days"/></FormField>
          <FormField label="Minimum payout ($)"><Input type="number" step="0.01" value={settings.min_payout} onChange={(e) => setSettings({...settings, min_payout: Number(e.target.value)})}/></FormField>
          <FormField label="Repeat purchase mode">
            <select value={settings.repeat_mode} onChange={(e) => setSettings({...settings, repeat_mode: e.target.value})} data-testid="set-repeat-mode" className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option value="first">First order only</option><option value="window">Attribution window (default)</option><option value="permanent">Permanent — affiliate owns customer</option>
            </select>
          </FormField>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={settings.allow_self_referral} onChange={(e) => setSettings({...settings, allow_self_referral: e.target.checked})}/> Allow self-referrals</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={settings.signup_enabled} onChange={(e) => setSettings({...settings, signup_enabled: e.target.checked})} data-testid="set-signup-enabled"/> Public affiliate signup enabled</label>
        <FormField label="Terms text"><Textarea rows={4} value={settings.terms_text} onChange={(e) => setSettings({...settings, terms_text: e.target.value})}/></FormField>
        <Button onClick={saveSettings} data-testid="save-settings" className="bg-blue-600 hover:bg-blue-700 text-white">Save settings</Button>
      </div>
    </div>
  );
}
function FormField({ label, children }) {
  return <div><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</div>;
}

// ---------- Public application form ----------
export function AffiliateApplyPublic() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", website: "", social: "", promotion_plan: "", audience_size: "", notes: "", terms_accepted: false });
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) { toast.error("Please accept the affiliate terms"); return; }
    try { const r = await api.post("/affiliate-application", form); setDone(r.data); }
    catch (er) { toast.error(er.response?.data?.detail || "Submit failed"); }
  };
  if (done) return (
    <div className="max-w-2xl mx-auto py-20 px-6 text-center" data-testid="apply-success">
      <h1 className="font-display text-3xl font-semibold text-slate-900">Thanks — your application is in.</h1>
      <p className="mt-3 text-slate-600">We'll review it within a few business days and email you when it's decided.</p>
      <p className="mt-3 text-sm text-slate-500 font-mono">{done.application_id}</p>
    </div>
  );
  return (
    <div className="max-w-2xl mx-auto py-16 px-6" data-testid="affiliate-apply-page">
      <div className="eyebrow mb-2">Affiliate Program</div>
      <h1 className="font-display text-4xl font-semibold text-slate-900 tracking-tight">Become an affiliate.</h1>
      <p className="mt-3 text-slate-600">Earn a scaling commission on every research order you refer. Higher tiers, higher commissions — up to 25%.</p>
      <form onSubmit={submit} className="mt-8 space-y-3 border border-slate-200 rounded-md bg-white p-6">
        <div className="grid grid-cols-2 gap-3">
          <Input required placeholder="First name" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} data-testid="apply-fn"/>
          <Input required placeholder="Last name" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} data-testid="apply-ln"/>
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="apply-email" className="col-span-2"/>
          <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
          <Input placeholder="Website" value={form.website} onChange={(e) => setForm({...form, website: e.target.value})}/>
          <Input placeholder="Social profile(s)" className="col-span-2" value={form.social} onChange={(e) => setForm({...form, social: e.target.value})}/>
          <Input placeholder="Estimated audience size" className="col-span-2" value={form.audience_size} onChange={(e) => setForm({...form, audience_size: e.target.value})}/>
        </div>
        <Textarea rows={3} placeholder="How do you plan to promote Peps4Pets?" value={form.promotion_plan} onChange={(e) => setForm({...form, promotion_plan: e.target.value})}/>
        <Textarea rows={2} placeholder="Anything else we should know? (optional)" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
        <label className="flex items-start gap-2 text-sm text-slate-700 pt-2">
          <input type="checkbox" checked={form.terms_accepted} onChange={(e) => setForm({...form, terms_accepted: e.target.checked})} data-testid="apply-terms" className="mt-1"/>
          I have read and agree to the Affiliate Program Terms and understand that commissions may be reversed for refunded, fraudulent, cancelled, or otherwise ineligible transactions.
        </label>
        <Button type="submit" data-testid="apply-submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white">Submit application</Button>
      </form>
    </div>
  );
}
